import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import SoinsCatalog from "./SoinsCatalog";
import DevisGenerator from "./DevisGenerator";
import sha256 from "crypto-js/sha256";
import { DEFAULT_SOINS, DEFAULT_GROUPS } from "./DefaultData";
import { ArrowRightOnRectangleIcon } from "@heroicons/react/24/outline";

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD_HASH;
const LOCAL_STORAGE_AUTH_KEY = "adminDevisAuth";
const LOCAL_STORAGE_SOINS_KEY = "soinsCatalog";
const LOCAL_STORAGE_GROUPS_KEY = "groupList";

const MAX_ATTEMPTS = 6;
const LOCK_TIME_MS = 10 * 60 * 1000; // 10 minutes
const ATTEMPTS_KEY = "adminAttempts";
const LOCK_UNTIL_KEY = "adminLockUntil";

// Function to clear all old data and reset to fresh start
function clearOldDataAndReset() {
  try {
    // Remove old keys
    localStorage.removeItem("teethCodes");
    localStorage.removeItem("soinsCatalog");
    localStorage.removeItem(LOCAL_STORAGE_SOINS_KEY);
    localStorage.removeItem(LOCAL_STORAGE_GROUPS_KEY);
    
    // Refresh page
    window.location.reload();
  } catch (err) {
    console.error("❌ Error clearing data:", err);
  }
}

function safeLoadSoins() {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_SOINS_KEY);
    if (!raw) return DEFAULT_SOINS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_SOINS;
    
    // Migrate from old format (single group) to new format (multiple groups)
    return parsed.map(soin => {
      if (soin.groups && Array.isArray(soin.groups)) {
        return soin; // Already in new format
      }
      // Old format: has 'group' property or no groups at all
      return {
        ...soin,
        groups: soin.group ? [soin.group] : []
      };
    });
  } catch {
    return DEFAULT_SOINS;
  }
}

function safeLoadGroups() {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_GROUPS_KEY);
    if (!raw) return DEFAULT_GROUPS;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : DEFAULT_GROUPS;
  } catch {
    return DEFAULT_GROUPS;
  }
}

const AdminDevis = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("devis");
  const [soins, setSoins] = useState(() => safeLoadSoins());
  const [groups, setGroups] = useState(() => safeLoadGroups());
  const [toast, setToast] = useState(null);

  // Persist soins whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_SOINS_KEY, JSON.stringify(soins));
    } catch (err) {
      console.error("Failed to save soins", err);
    }
  }, [soins]);

  // Persist groups whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_GROUPS_KEY, JSON.stringify(groups));
    } catch (err) {
      console.error("Failed to save groups", err);
    }
  }, [groups]);

  // Check authentication on mount
  useEffect(() => {
    try {
      if (localStorage.getItem(LOCAL_STORAGE_AUTH_KEY) === "true") {
        setIsAuthenticated(true);
      }
    } catch {
      // ignore
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();

    const now = Date.now();
    const lockUntil = Number(localStorage.getItem(LOCK_UNTIL_KEY) || 0);

    if (now < lockUntil) {
      const minutes = Math.ceil((lockUntil - now) / 60000);
      setError(`Trop de tentatives. Réessayez dans ${minutes} min.`);
      return;
    }

    if (sha256(password).toString() === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setError("");
      localStorage.removeItem(ATTEMPTS_KEY);
      localStorage.removeItem(LOCK_UNTIL_KEY);
      try {
        localStorage.setItem(LOCAL_STORAGE_AUTH_KEY, "true");
      } catch {
        // ignore
      }
      return;
    }

    const attempts = Number(localStorage.getItem(ATTEMPTS_KEY) || 0) + 1;
    localStorage.setItem(ATTEMPTS_KEY, attempts);

    if (attempts >= MAX_ATTEMPTS) {
      localStorage.setItem(LOCK_UNTIL_KEY, now + LOCK_TIME_MS);
      setError("Trop de tentatives. Accès bloqué temporairement.");
    } else {
      setError(`Mot de passe incorrect. (${MAX_ATTEMPTS - attempts} essais restants)`);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    try {
      localStorage.removeItem(LOCAL_STORAGE_AUTH_KEY);
    } catch {
      // ignore
    }
  };

  const handleSoinsChange = (updatedSoins) => {
    setSoins(updatedSoins);
  };

  const handleGroupsChange = (updatedGroups) => {
    setGroups(updatedGroups);
  };

  const handleExport = () => {
    const data = {
      version: "2.0",
      soins,
      groups,
      exportedAt: new Date().toISOString(),
    };
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `devis-backup-${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImport = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result;
        if (typeof content !== "string") {
          setToast({ message: "Erreur : fichier invalide", type: "error" });
          setTimeout(() => setToast(null), 3000);
          return;
        }
        const data = JSON.parse(content);

        const migrateSoins = (soinsArray) => {
          return soinsArray.map(soin => {
            if (soin.groups && Array.isArray(soin.groups)) {
              return soin; // Already in new format
            }
            // Old format: has 'group' property
            return {
              ...soin,
              groups: soin.group ? [soin.group] : []
            };
          });
        };

        // New format: version 2.0 with soins and groups
        if (data.version === "2.0" && Array.isArray(data.soins)) {
          const migrated = migrateSoins(data.soins);
          setSoins(migrated);
          if (Array.isArray(data.groups)) {
            setGroups(data.groups);
          }
          setToast({ message: "Données importées avec succès!", type: "success" });
          setTimeout(() => setToast(null), 3000);
        }
        // Old format: soins + teethCodes
        else if (Array.isArray(data.soins)) {
          const migrated = migrateSoins(data.soins);
          setSoins(migrated);
          setToast({ message: "Données importées avec succès (ancien format)", type: "success" });
          setTimeout(() => setToast(null), 3000);
        }
        // Very old format: just an array
        else if (Array.isArray(data)) {
          const migrated = migrateSoins(data);
          setSoins(migrated);
          setToast({ message: "Données importées avec succès", type: "success" });
          setTimeout(() => setToast(null), 3000);
        } else {
          setToast({ message: "Erreur : format de fichier invalide", type: "error" });
          setTimeout(() => setToast(null), 3000);
        }
      } catch (err) {
        setToast({ 
          message: `Erreur lors de l'importation : ${err instanceof Error ? err.message : "erreur inconnue"}`, 
          type: "error" 
        });
        setTimeout(() => setToast(null), 3000);
      }
    };
    reader.readAsText(file);
    event.target.value = "";
  };

  const renderContent = () => {
    if (activeTab === "catalog") {
      return (
        <SoinsCatalog
          soins={soins}
          groups={groups}
          onSoinsChange={handleSoinsChange}
          onGroupsChange={handleGroupsChange}
          onExport={handleExport}
          onImport={handleImport}
          onReset={clearOldDataAndReset}
        />
      );
    }
    return <DevisGenerator soins={soins} groupOrder={groups} />;
  };

  if (!isAuthenticated) {
    return (
      <>
        <Helmet>
          <title>Internal Devis Manager</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <section className="section-padding bg-gray-50 min-h-screen">
          <div className="container-custom flex items-center justify-center">
            <div className="w-full max-w-md bg-white rounded-xl shadow-md p-8 space-y-6 card-hover">
              <h1 className="text-2xl font-semibold text-gray-800 text-center">
                Accès réservé
              </h1>
              <p className="text-sm text-gray-500 text-center">
                Entrez le mot de passe pour accéder à l'espace interne de
                gestion des devis.
              </p>
              <form onSubmit={handleLogin} className="space-y-4 admin-controls">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mot de passe
                  </label>
                  <input
                    type="password"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                <button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg transition-colors duration-200"
                >
                  Se connecter
                </button>
              </form>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Internal Devis Manager</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <section className="section-padding bg-gray-50 min-h-screen">
        <div className="container-custom space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">
                Gestion interne des devis
              </h1>
              <p className="text-sm text-gray-500">
                Catalogue des soins et génération de devis.
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
              title="Se déconnecter"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Déconnexion
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-md">
            <div className="border-b border-gray-200 flex admin-controls">
                            <button
                type="button"
                onClick={() => setActiveTab("devis")}
                className={`px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "devis"
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Générateur de devis
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("catalog")}
                className={`px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "catalog"
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Catalogue des soins
              </button>

            </div>
            <div className="p-4 sm:p-6">{renderContent()}</div>
          </div>
        </div>
      </section>

      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-6 right-6 z-50">
          <div className={`px-6 py-3 rounded-lg shadow-lg text-white font-medium flex items-center gap-2 animate-fade-in ${
            toast.type === "success" 
              ? "bg-green-500 hover:bg-green-600" 
              : "bg-red-500 hover:bg-red-600"
          }`}>
            {toast.message}
          </div>
        </div>
      )}
    </>
  );
};

export default AdminDevis;